import { ActionInput, ActionTextarea, ActionButton } from '../parts/index.js';
import { useEffect, useState } from 'react';
import { closeApi, type CloseKind, type CloseSaved, type CloseState, type LostReason } from './close-api.js';
import type { LiveContext } from './LiveCard.js';
import type { HeatRow } from './api.js';
import { moneyFull } from './heat-words.js';

export const REASONS: Array<{ reason: LostReason; words: string }> = [
  { reason: 'chose_another_supplier', words: 'Chose another supplier' }, { reason: 'price', words: 'Price' },
  { reason: 'no_budget', words: 'No budget this year' }, { reason: 'stopped_replying', words: 'Stopped replying' },
];
export const lostValidation = (reason: LostReason | null, note: string): string | null =>
  !reason ? 'Choose why the deal was lost.' : reason === 'other' && !note.trim() ? 'Say why in your words.' : null;

function today(now: string): string {
  const d = new Date(now);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function CloseBox({ kind, row, ctx, cancel }: { kind: CloseKind; row: HeatRow; ctx: LiveContext; cancel: () => void }) {
  const [state, setState] = useState<CloseState | null>(null);
  const [amount, setAmount] = useState(row.figure?.amount ?? '');
  const [signedOn, setSignedOn] = useState(today(ctx.now));
  const [reason, setReason] = useState<LostReason | null>(null);
  const [note, setNote] = useState('');
  const [wrong, setWrong] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setState(null);
    void closeApi.state(ctx.store, row.id).then((s) => { if (active) { setState(s); setAmount(s.revenueYearAmount ?? ''); setWrong(null); } })
      .catch((e: unknown) => { if (active) setWrong(e instanceof Error ? e.message : 'Not saved. Try again.'); });
    return () => { active = false; };
  }, [ctx.store, row.id, retry]);
  const save = async () => {
    const validation = kind === 'lost' ? lostValidation(reason, note) : null;
    if (validation) { setWrong(validation); return; }
    if (!state) return;
    setBusy(true); setWrong(null);
    try {
      const saved: CloseSaved = kind === 'won'
        ? await closeApi.won(ctx.store, row.id, { revenueYearAmount: amount.trim() ? Number(amount) : null, signedOn, expectedUpdatedAt: state.updatedAt })
        : await closeApi.lost(ctx.store, row.id, { reason: reason!, ...(note.trim() ? { note: note.trim() } : {}), expectedUpdatedAt: state.updatedAt });
      ctx.setNote(row.id, { saved: { field: `close:${kind}`, changeId: saved.actionId, words: `Marked ${row.organisationName ?? row.product ?? row.title} ${kind}.`, undoUntil: saved.undoUntil }, note: null });
      cancel(); ctx.refresh();
    } catch (e) { setBusy(false); setWrong(e instanceof Error ? e.message : 'Not saved. Try again.'); }
  };
  const company = row.organisationName ?? row.product ?? row.title;
  return <form className="dc-closebox" onSubmit={(e) => { e.preventDefault(); void save(); }}>
    <h4>{kind === 'won' ? 'Mark it won' : 'Mark it lost. Why?'}</h4>
    {kind === 'won' ? <>
      <div className="dc-fields">
        <label>{moneyFull('0', state?.currency ?? row.figure?.currency ?? ctx.homeCurrency).replace(/[\d,.\s]+$/, '')} a year (optional)
          <ActionInput type="number" min="0" step="0.01" value={amount} disabled={busy || !state} onChange={(e) => setAmount(e.target.value)} />
        </label>
        <label>Signed on <ActionInput type="date" required value={signedOn} disabled={busy} onChange={(e) => setSignedOn(e.target.value)} /></label>
      </div>
      {state ? <p className="lv-meta">{state.signedCopyHeld
        ? 'A signed copy is already filed for this deal. Saving files it as the contract.'
        : `No signed copy is filed yet. The deal shows "Won, contract to come" and Cobrain files the copy when it arrives in mail. ${company} becomes a client and the figure moves to its forecast. Without a figure the win is counted when one is added. You can undo this for a week.`}</p> : <p className="lv-meta">Reading the deal</p>}
    </> : <>
      <div className="dc-reasons" role="group" aria-label="Mark it lost. Why?">
        {REASONS.map((x) => <ActionButton key={x.reason} type="button" className="opt" aria-pressed={reason === x.reason} disabled={busy} onClick={() => { setReason(x.reason); setWrong(null); }}>{x.words}</ActionButton>)}
      </div>
      <label className="dc-other"><ActionInput type="radio" name={`lost-${row.id}`} checked={reason === 'other'} disabled={busy} onChange={() => { setReason('other'); setWrong(null); }} />In your words, if none of these fits</label>
      <ActionTextarea aria-label="In your words, if none of these fits" rows={2} value={note} required={reason === 'other'} disabled={busy}
        onChange={(e) => { setNote(e.target.value); if (e.target.value.trim()) setReason('other'); }} />
      <p className="lv-meta">The line leaves the list and goes to Closed. You can undo this for a week.</p>
    </>}
    {wrong ? <p className="lv-said" role="alert">{wrong} <ActionButton reading type="button" className="hx-link" onClick={() => { setWrong(null); setRetry((n) => n + 1); }}>Try again</ActionButton></p> : null}
    <div className="dc-options"><ActionButton className="opt" type="submit" disabled={busy || !state}>{kind === 'won' ? 'Save as won' : 'Save as lost'}</ActionButton><ActionButton className="opt" type="button" disabled={busy} onClick={cancel}>Cancel</ActionButton></div>
  </form>;
}

/** Kept by the page so closing a line cannot take its toast and Undo away. */
export function CloseToasts({ ctx }: { ctx: LiveContext }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);
  return <>{Object.entries(ctx.notes).filter(([, n]) => n.saved?.field.startsWith('close:')).map(([id, n]) => {
    const saved = n.saved!;
    const can = !!saved.undoUntil && Date.parse(saved.undoUntil) > Date.now();
    return <p className="dc-toast" role="status" key={id}>{saved.words} {can ? <ActionButton className="hx-link" type="button" disabled={busy === id} onClick={() => {
      setBusy(id); setWrong(null);
      void closeApi.undo(ctx.store, id, saved.field.slice(6) as CloseKind).then(() => { ctx.setNote(id, null); setBusy(null); ctx.refresh(); })
        .catch((e: unknown) => { setBusy(null); setWrong(e instanceof Error ? e.message : 'Not saved. Try again.'); });
    }}>Undo</ActionButton> : 'To reopen it, open the deal'}{wrong ? <span role="alert"> {wrong}</span> : null}</p>;
  })}</>;
}
